import { Component, useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { formatMoney, formatCurrencyTotals } from "@/lib/currency";

// Every department report is *self-generated* — it's computed live from the
// same data every other page in this app already writes (jobs, QC decisions,
// GINs/GRNs, sales orders, leads, ...). Nobody fills this in by hand.
// See backend/daily_reports.py.
const DEPT_LABELS = {
  production: "Production",
  quality: "Quality",
  stores: "Stores / Inventory",
  sales: "Sales & CRM",
  marketing: "Marketing",
};

// Daily values come per currency (`*_by_currency`, e.g. { INR, USD }) — INR
// and USD are shown side by side, never added. Reports generated before
// currency support only have the plain (INR) value.
const money = (n, byCurrency) => (byCurrency && Object.keys(byCurrency).length
  ? formatCurrencyTotals(byCurrency)
  : formatMoney(n, "INR"));
const todayStr = () => new Date().toISOString().slice(0, 10);

function StatTile({ label, value, tid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={tid}>
      <div className="text-xs uppercase tracking-[0.12em] text-slate-500">{label}</div>
      <div className="text-xl font-bold text-slate-900 mt-1 mono">{value ?? "—"}</div>
    </div>
  );
}

function SummaryBanner({ summary }) {
  if (!summary?.length) return null;
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-1.5" data-testid="report-summary">
      {summary.map((line, i) => (
        <div key={i} className="text-sm text-blue-900 flex gap-2">
          <span className="text-blue-400">•</span>
          <span>{line}</span>
        </div>
      ))}
    </div>
  );
}

function MiniTable({ title, rows, columns, tid }) {
  if (!rows?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={tid}>
      <div className="px-4 py-2.5 border-b border-slate-100 text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">
        {title}
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className="text-left px-4 py-2 font-medium">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100">
              {columns.map((c) => (
                <td key={c.key} className="px-4 py-2 text-slate-800">{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Every renderer below defaults `m` to {} and reads nested fields with
// optional chaining / nullish fallbacks. This is deliberate defensiveness,
// not decoration: these components are only ever supposed to receive the
// metrics shape their own department returns, but if a tab switch or a
// failed refetch ever hands one renderer another department's shape (or no
// data at all) mid-flight, a bare `m.decisions_today.approved`-style access
// throws and — with nothing catching it — takes the *entire* app down to a
// blank screen, not just this panel. See the page component below for the
// actual fix (state is cleared before every fetch so this should no longer
// happen), and ReportErrorBoundary for the last-resort net if it ever does.
function ProductionReport({ m = {} }) {
  const activeByStage = (m.active_jobs_by_stage || []).filter((s) => s.count > 0);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Jobs Started Today" value={m.jobs_started_today} tid="stat-jobs-started" />
        <StatTile label="Jobs Closed Today" value={m.jobs_closed_today} tid="stat-jobs-closed" />
        <StatTile label="Active Jobs" value={m.active_jobs_total} tid="stat-jobs-active" />
        <StatTile label="Output Today" value={m.output_today} tid="stat-output" />
        <StatTile label="Rejected Today" value={m.rejected_today} tid="stat-rejected" />
        <StatTile label="Wastage Today" value={m.wastage_today} tid="stat-wastage" />
        <StatTile label="Rejection Rate" value={m.rejection_rate_pct != null ? `${m.rejection_rate_pct}%` : undefined} tid="stat-rejection-rate" />
      </div>
      <MiniTable
        title="Active Jobs by Stage"
        rows={activeByStage}
        columns={[{ key: "label", label: "Stage" }, { key: "count", label: "Active Jobs" }]}
        tid="table-active-by-stage"
      />
      <MiniTable
        title="Output by Operator (Today)"
        rows={m.by_operator}
        columns={[
          { key: "operator", label: "Operator" },
          { key: "produced", label: "Produced" },
          { key: "rejected", label: "Rejected" },
        ]}
        tid="table-by-operator"
      />
    </div>
  );
}

function QualityReport({ m = {} }) {
  const decisions = m.decisions_today || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Decisions Today" value={m.total_decided_today} tid="stat-decisions" />
        <StatTile label="Approved" value={decisions.approved} tid="stat-approved" />
        <StatTile label="Rejected" value={decisions.rejected} tid="stat-rejected-qc" />
        <StatTile label="Rework" value={decisions.rework} tid="stat-rework" />
        <StatTile label="Pass Rate" value={m.pass_rate_pct != null ? `${m.pass_rate_pct}%` : undefined} tid="stat-pass-rate" />
        <StatTile label="Pending QC Backlog" value={m.pending_qc_backlog} tid="stat-pending-qc" />
      </div>
      <MiniTable
        title="Decisions by Stage (Today)"
        rows={m.by_stage}
        columns={[
          { key: "label", label: "Stage" },
          { key: "approved", label: "Approved" },
          { key: "rejected", label: "Rejected" },
          { key: "rework", label: "Rework" },
        ]}
        tid="table-qc-by-stage"
      />
      <MiniTable
        title="Decisions by Inspector (Today)"
        rows={m.by_inspector}
        columns={[
          { key: "inspector", label: "Inspector" },
          { key: "approved", label: "Approved" },
          { key: "rejected", label: "Rejected" },
          { key: "rework", label: "Rework" },
        ]}
        tid="table-qc-by-inspector"
      />
    </div>
  );
}

function StoresReport({ m = {} }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <StatTile label="GINs Received" value={m.gins_created_today} tid="stat-gins" />
      <StatTile label="GRNs Inspected" value={m.grns_created_today} tid="stat-grns" />
      <StatTile label="Purchase Requests" value={m.purchase_requests_today} tid="stat-prs" />
      <StatTile label="Purchase Orders" value={m.purchase_orders_today} tid="stat-pos" />
      <StatTile label="PO Value Today" value={money(m.purchase_orders_value_today, m.purchase_orders_value_today_by_currency)} tid="stat-po-value" />
      <StatTile label="Stock In / Out" value={`+${m.stock_inward_today ?? 0} / -${m.stock_outward_today ?? 0}`} tid="stat-stock-io" />
      <StatTile label="Movement Entries" value={m.inventory_movements_today} tid="stat-movements" />
      <StatTile label="Low Stock Items" value={m.low_stock_items} tid="stat-low-stock" />
    </div>
  );
}

function SalesReport({ m = {} }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <StatTile label="Quotations Raised" value={m.quotations_created_today} tid="stat-quotations" />
      <StatTile label="Quotations Sent" value={m.quotations_sent_today} tid="stat-quotations-sent" />
      <StatTile label="Sales Order Booked" value={m.customer_sales_orders_today} tid="stat-cso" />
      <StatTile label="Sales Order Value" value={money(m.customer_sales_orders_value_today, m.customer_sales_orders_value_today_by_currency)} tid="stat-cso-value" />
      <StatTile label="Transfer Orders" value={m.transfer_orders_today} tid="stat-transfer-orders" />
      <StatTile label="Deliveries Dispatched" value={m.delivery_challans_dispatched_today} tid="stat-dc-dispatched" />
      <StatTile label="Tax Invoices Raised" value={m.tax_invoices_today} tid="stat-tax-invoices" />
      <StatTile label="Invoiced Value" value={money(m.tax_invoices_value_today, m.tax_invoices_value_today_by_currency)} tid="stat-invoiced-value" />
    </div>
  );
}

function MarketingReport({ m = {} }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="New Leads Today" value={m.new_leads_today} tid="stat-new-leads" />
        <StatTile label="Activities Logged" value={m.activities_logged_today} tid="stat-activities" />
        <StatTile label="Activities Completed" value={m.activities_completed_today} tid="stat-activities-done" />
        <StatTile label="Leads Won (All Time)" value={m.leads_won_all_time} tid="stat-leads-won" />
      </div>
      <MiniTable
        title="New Leads by Source (Today)"
        rows={m.leads_by_source_today}
        columns={[{ key: "source", label: "Source" }, { key: "count", label: "Leads" }]}
        tid="table-leads-by-source"
      />
    </div>
  );
}

const REPORT_RENDERERS = {
  production: ProductionReport,
  quality: QualityReport,
  stores: StoresReport,
  sales: SalesReport,
  marketing: MarketingReport,
};

// Last-resort net: if a renderer above still throws (a metrics shape we
// didn't anticipate), this catches it and shows an inline message scoped to
// the report panel instead of an uncaught render error unmounting the whole
// app (sidebar included) — which is what happened before this fix.
class ReportErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidUpdate(prevProps) {
    // Reset once the parent hands us a new report (new tab/date/data) so a
    // fixed or different department can render again without a full reload.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }
  componentDidCatch(error) {
    console.error("Daily report render error:", error);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800" data-testid="report-render-error">
          This report couldn't be displayed. Try switching tabs or clicking "Generate Now" again.
        </div>
      );
    }
    return this.props.children;
  }
}

export default function DailyDepartmentReports() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [date, setDate] = useState(todayStr());
  const [departments, setDepartments] = useState([]);
  const [activeTab, setActiveTab] = useState(null); // dept key, or "__all__" for admin combined
  const [report, setReport] = useState(null);
  const [allReports, setAllReports] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/daily-reports/departments");
        setDepartments(data);
        const accessible = data.filter((d) => d.accessible);
        setActiveTab(isAdmin ? "__all__" : (accessible[0]?.key || null));
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Could not load departments");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadReport = useCallback(async () => {
    if (!activeTab) return;
    // Clear whatever the previous tab/date left behind *before* the new
    // request goes out — otherwise a slow or failed fetch for the new tab
    // leaves the old tab's data sitting in state, and it gets rendered
    // through the new tab's (mismatched) renderer. That's exactly what
    // produced the blank-page crash: switching to Quality while Production's
    // report was still in state fed Production's shape into QualityReport,
    // which threw on a field Production's metrics don't have.
    setReport(null);
    setAllReports(null);
    setError("");
    setLoading(true);
    try {
      if (activeTab === "__all__") {
        const { data } = await api.get("/daily-reports/summary", { params: { date } });
        setAllReports(data);
      } else {
        const { data } = await api.get(`/daily-reports/${activeTab}`, { params: { date } });
        setReport(data);
      }
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Could not load report";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [activeTab, date]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const regenerate = async () => {
    if (!activeTab || activeTab === "__all__") {
      await loadReport();
      if (!error) toast.success("Reports refreshed");
      return;
    }
    setGenerating(true);
    setError("");
    try {
      const { data } = await api.post(`/daily-reports/${activeTab}/generate`, null, { params: { date } });
      setReport(data);
      toast.success("Report regenerated");
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Could not regenerate report";
      setError(msg);
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const accessibleDepts = useMemo(() => departments.filter((d) => d.accessible), [departments]);

  return (
    <div className="space-y-6" data-testid="daily-reports-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Daily Department Reports</h1>
          <p className="text-sm text-slate-500 mt-0.5">Self-generated from today's live data — nothing here is typed in by hand.</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={date}
            max={todayStr()}
            onChange={(e) => setDate(e.target.value)}
            className="border border-slate-300 rounded-md px-3 py-1.5 text-sm"
            data-testid="report-date-input"
          />
          <Button variant="outline" size="sm" onClick={regenerate} disabled={generating} data-testid="regenerate-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${generating ? "animate-spin" : ""}`} />
            Generate Now
          </Button>
        </div>
      </div>

      {activeTab && (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            {isAdmin && <TabsTrigger value="__all__" data-testid="tab-all">All Departments</TabsTrigger>}
            {accessibleDepts.map((d) => (
              <TabsTrigger key={d.key} value={d.key} data-testid={`tab-${d.key}`}>{d.label}</TabsTrigger>
            ))}
          </TabsList>

          {loading && <div className="text-sm text-slate-500 mt-4" data-testid="report-loading">Generating report…</div>}

          {!loading && error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2 text-sm text-red-800" data-testid="report-error">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && activeTab === "__all__" && allReports && (
            <TabsContent value="__all__" className="mt-4 space-y-6">
              {allReports.departments.map((dep) => {
                const Renderer = REPORT_RENDERERS[dep.department];
                return (
                  <div key={dep.department} className="space-y-3" data-testid={`all-dept-${dep.department}`}>
                    <h2 className="text-lg font-semibold text-slate-800">{DEPT_LABELS[dep.department] || dep.department}</h2>
                    <SummaryBanner summary={dep.summary} />
                    <ReportErrorBoundary resetKey={`${dep.department}:${allReports.date}`}>
                      {Renderer && <Renderer m={dep.metrics} />}
                    </ReportErrorBoundary>
                  </div>
                );
              })}
            </TabsContent>
          )}

          {!loading && !error && activeTab !== "__all__" && report && report.department === activeTab && (
            <TabsContent value={activeTab} className="mt-4 space-y-4">
              <SummaryBanner summary={report.summary} />
              <ReportErrorBoundary resetKey={`${report.department}:${report.date}`}>
                {(() => {
                  const Renderer = REPORT_RENDERERS[activeTab];
                  return Renderer ? <Renderer m={report.metrics} /> : null;
                })()}
              </ReportErrorBoundary>
              <div className="text-xs text-slate-400">
                Generated {new Date(report.generated_at).toLocaleString()} by {report.generated_by}
              </div>
            </TabsContent>
          )}
        </Tabs>
      )}

      {!activeTab && !loading && (
        <div className="text-sm text-slate-500" data-testid="no-department-access">
          No department report is available for your role yet.
        </div>
      )}
    </div>
  );
}
